/**
 * The patient list (§7, Phase 3).
 *
 * A patient is a code and nothing else (constraint 1). There is no name column
 * because there is no name anywhere in this system — the doctor recognises the
 * code from his separate offline practice app, which holds the only mapping.
 *
 * Codes are typed in, never generated here (§2). Generating them on this
 * platform would put the generator and the list in the same place.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type PatientSummary } from '../../api.js';
import styles from './PatientList.module.css';

interface PatientsResponse {
  patients: PatientSummary[];
}

/** §7: the dashboard flags an expiry a week out, so nobody is cut off mid-program. */
const EXPIRING_SOON_DAYS = 14;

export function PatientList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  /** The patient the delete confirmation is open for, if any. */
  const [deleting, setDeleting] = useState<PatientSummary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const patients = useQuery({
    queryKey: ['patients'],
    queryFn: () => api.get<PatientsResponse>('/patients'),
    retry: false,
  });

  const addPatient = useMutation({
    mutationFn: (newCode: string) =>
      api.post<{ id: string }>('/patients', { code: newCode }),
    onSuccess: () => {
      setCode('');
      setAddError(null);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'code_already_exists') {
        setAddError('That code is already on the list.');
      } else if (error instanceof ApiError && error.code === 'invalid_code_format') {
        setAddError('Codes look like XX-0001 — two letters, a dash, four digits.');
      } else {
        setAddError('Could not add that code. Try again.');
      }
    },
  });

  /**
   * Access expiry (constraint 13). A DATE and nothing else — there is no
   * payment, price, or subscription anywhere in this system. Clearing the
   * field means no expiry.
   */
  const setExpiry = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string | null }) =>
      api.patch(`/patients/${id}`, { accessExpiresOn: date }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['patients'] }),
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post(`/patients/${id}/${active ? 'reactivate' : 'deactivate'}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['patients'] }),
  });

  /**
   * The only destructive call this application makes. Deactivated patients
   * only; the server refuses an active one regardless of what is sent.
   */
  const deletePatient = useMutation({
    mutationFn: (id: string) => api.del(`/patients/${id}`),
    onSuccess: () => {
      setDeleting(null);
      setDeleteError(null);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
    onError: (error: unknown) => {
      setDeleteError(
        error instanceof ApiError && error.code === 'patient_is_active'
          ? 'That patient is active again. Deactivate it first.'
          : 'Could not remove it. Nothing was changed.',
      );
    },
  });

  if (patients.isError) {
    if (patients.error instanceof ApiError && patients.error.status === 401) {
      navigate('/clinician/login', { replace: true });
      return null;
    }
    return <p className={styles.state}>Could not load the patient list.</p>;
  }

  const rows = patients.data?.patients ?? [];
  // A code is the only thing there is to search on — there is no name, and
  // there never will be (constraint 1). Substring rather than prefix, because
  // the memorable part of a code is usually not its start.
  const needle = search.trim().toUpperCase();
  const matching = needle
    ? rows.filter((p) => p.code.toUpperCase().includes(needle))
    : rows;
  const active = matching.filter((p) => !p.deactivatedAt);
  const inactive = matching.filter((p) => p.deactivatedAt);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Patients</h1>
      </header>

      <form
        className={styles.addRow}
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = code.trim().toUpperCase();
          if (trimmed) addPatient.mutate(trimmed);
        }}
      >
        <label className={styles.addLabel} htmlFor="new-code">
          Add a patient code
          <span className={styles.addHelp}>
            Generated in your practice app, then typed in here. Nothing else about
            the patient is stored.
          </span>
        </label>
        <div className={styles.addControls}>
          <input
            id="new-code"
            className={styles.codeInput}
            value={code}
            placeholder="XX-0000"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setAddError(null);
            }}
          />
          <button
            className={styles.primaryButton}
            type="submit"
            disabled={addPatient.isPending || code.trim().length === 0}
          >
            {addPatient.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
        {addError && <p className={styles.addError}>{addError}</p>}
      </form>

      <div className={styles.searchRow}>
        <label className={styles.addLabel} htmlFor="patient-search">
          Find a code
        </label>
        <div className={styles.addControls}>
          <input
            id="patient-search"
            className={styles.codeInput}
            type="search"
            value={search}
            placeholder="Any part of a code"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => setSearch(e.target.value)}
          />
          {needle && (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setSearch('')}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {patients.isPending ? (
        <p className={styles.state}>Loading…</p>
      ) : rows.length === 0 ? (
        <p className={styles.state}>
          No patients yet. Add a code above to write the first prescription.
        </p>
      ) : (
        <>
          {needle && active.length === 0 && inactive.length === 0 && (
            <p className={styles.state}>No code contains “{needle}”.</p>
          )}

          <section className={styles.section}>
            <SectionHeading
              open={showActive}
              onToggle={() => setShowActive((v) => !v)}
              label="Active"
              count={active.length}
            />
            {showActive &&
              (active.length === 0 ? (
                <p className={styles.state}>
                  {needle ? 'No active code matches.' : 'No active patients.'}
                </p>
              ) : (
                <PatientTable
                  rows={active}
                  onSetActive={(id, isActive) => setActive.mutate({ id, active: isActive })}
                  onSetExpiry={(id, date) => setExpiry.mutate({ id, date })}
                  busy={setActive.isPending}
                />
              ))}
          </section>

          {inactive.length > 0 && (
            <section className={styles.inactiveSection}>
              <SectionHeading
                open={showInactive}
                onToggle={() => setShowInactive((v) => !v)}
                label="Deactivated"
                count={inactive.length}
              />
              {showInactive && (
                <>
                  <p className={styles.subhelp}>
                    Their codes no longer work. Deactivating removes nothing —
                    every prescription and every result is still here, and
                    reactivating puts them straight back. Remove permanently is
                    the one thing here that cannot be undone.
                  </p>
                  <PatientTable
                    rows={inactive}
                    onSetActive={(id, isActive) => setActive.mutate({ id, active: isActive })}
                    onSetExpiry={(id, date) => setExpiry.mutate({ id, date })}
                    onDelete={(patient) => {
                      setDeleteError(null);
                      setDeleting(patient);
                    }}
                    busy={setActive.isPending}
                  />
                </>
              )}
            </section>
          )}

          {deleting && (
            <DeleteDialog
              patient={deleting}
              busy={deletePatient.isPending}
              error={deleteError}
              onCancel={() => {
                setDeleting(null);
                setDeleteError(null);
              }}
              onConfirm={() => deletePatient.mutate(deleting.id)}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * A section that folds away. Both lists grow without limit — nothing is ever
 * removed from the deactivated one by ordinary use — so the page needs to be
 * readable when there are eighty codes on it.
 */
function SectionHeading({
  open,
  onToggle,
  label,
  count,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  count: number;
}) {
  return (
    <h2 className={styles.subheading}>
      <button
        type="button"
        className={styles.disclosure}
        aria-expanded={open}
        onClick={onToggle}
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className={styles.srOnly}>{open ? 'Collapse' : 'Expand'} {label}</span>
      </button>
      {label} — {count}
    </h2>
  );
}

function PatientTable({
  rows,
  onSetActive,
  onSetExpiry,
  onDelete,
  busy,
}: {
  rows: PatientSummary[];
  onSetActive: (id: string, active: boolean) => void;
  onSetExpiry: (id: string, date: string | null) => void;
  /** Only supplied for the deactivated list. Absent means the row cannot be removed. */
  onDelete?: (patient: PatientSummary) => void;
  busy: boolean;
}) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th scope="col">Code</th>
          <th scope="col">Program</th>
          <th scope="col">Access</th>
          <th scope="col">
            <span className={styles.srOnly}>Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((patient) => (
          <tr key={patient.id} className={patient.deactivatedAt ? styles.dimmed : undefined}>
            <th scope="row" className={styles.code}>
              {patient.code}
            </th>
            <td>
              <ProgramCell patient={patient} />
            </td>
            <td>
              <AccessCell
                patient={patient}
                onSetExpiry={(date) => onSetExpiry(patient.id, date)}
              />
            </td>
            <td className={styles.actions}>
              <PrescriptionLink patient={patient} />
              <button
                type="button"
                className={styles.linkButton}
                disabled={busy}
                onClick={() => onSetActive(patient.id, patient.deactivatedAt !== null)}
              >
                {patient.deactivatedAt ? 'Reactivate' : 'Deactivate'}
              </button>
              {onDelete && (
                <button
                  type="button"
                  className={styles.destructive}
                  onClick={() => onDelete(patient)}
                >
                  Remove permanently
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The confirmation for the one irreversible action in this application.
 *
 * It asks for the code to be typed rather than for a button to be clicked.
 * Everywhere else a confirmation prompt would be friction charged against a
 * correct action (constraint 4) and there deliberately is none — but this is
 * the single case where the cost of a mis-click is unbounded and unrecoverable,
 * so the friction is the feature.
 */
function DeleteDialog({
  patient,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  patient: PatientSummary;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const matches = typed.trim().toUpperCase() === patient.code.toUpperCase();

  return (
    <div
      className={styles.dialogBackdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-heading"
    >
      <div className={styles.dialog}>
        <h2 className={styles.dialogHeading} id="delete-heading">
          Remove {patient.code} permanently
        </h2>
        <p className={styles.dialogBody}>
          This deletes the patient and everything attached to them — every
          prescription, every session they completed, and every result. It cannot
          be undone from inside this application, and there is no copy kept
          anywhere else. Only the entry in the access log recording that you did
          it will remain.
        </p>
        <p className={styles.dialogBody}>
          If you might want any of it back, close this and leave them
          deactivated. A deactivated code does not work and costs nothing to keep.
        </p>
        <label className={styles.addLabel} htmlFor="delete-confirm">
          Type <strong>{patient.code}</strong> to confirm
        </label>
        <input
          id="delete-confirm"
          className={styles.codeInput}
          value={typed}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => setTyped(e.target.value.toUpperCase())}
        />
        {error && <p className={styles.addError}>{error}</p>}
        <div className={styles.dialogActions}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.destructiveButton}
            onClick={onConfirm}
            disabled={busy || !matches}
          >
            {busy ? 'Removing…' : 'Remove permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProgramCell({ patient }: { patient: PatientSummary }) {
  if (patient.publishedVersion === null) {
    return (
      <span className={styles.muted}>
        {patient.hasDraft ? 'Draft, not yet published' : 'None yet'}
      </span>
    );
  }
  return (
    <span>
      Version {patient.publishedVersion}
      {patient.hasDraft && <span className={styles.draftFlag}>unpublished edits</span>}
    </span>
  );
}

/**
 * Access expiry is a date the doctor sets, and nothing more (constraint 13).
 * Clearing the field means no expiry. There is no payment, price, or
 * subscription anywhere near this.
 *
 * Edited in place rather than behind an edit mode: it is one field, changed
 * rarely, and a two-click round trip to set a date the doctor already knows
 * would be friction for its own sake (constraint 4).
 */
/**
 * One link, two states, two colours.
 *
 * A patient with no program is the thing worth spotting while scanning a list —
 * somebody added last week who never got a prescription — and before this the
 * only difference was the word, so finding them meant reading every row.
 *
 * The words still differ, so colour is a second carrier rather than the only
 * one. That matters: the low-stimulus theme desaturates the chrome, and a
 * distinction carried by hue alone would disappear in it.
 */
function PrescriptionLink({ patient }: { patient: PatientSummary }) {
  const unwritten = patient.publishedVersion === null && !patient.hasDraft;
  return (
    <Link
      className={unwritten ? styles.actionUnwritten : styles.action}
      to={`/clinician/patients/${patient.id}`}
    >
      {unwritten ? 'Write prescription' : 'Open prescription'}
    </Link>
  );
}

function AccessCell({
  patient,
  onSetExpiry,
}: {
  patient: PatientSummary;
  onSetExpiry: (date: string | null) => void;
}) {
  return (
    <div className={styles.accessCell}>
      <input
        type="date"
        className={styles.dateInput}
        value={patient.accessExpiresOn ?? ''}
        aria-label={`Access expiry for ${patient.code}`}
        onChange={(e) => onSetExpiry(e.target.value === '' ? null : e.target.value)}
      />
      <AccessStatus expiresOn={patient.accessExpiresOn} />
    </div>
  );
}

/** The words beside the date. Never colour alone — each state is also named. */
function AccessStatus({ expiresOn }: { expiresOn: string | null }) {
  if (!expiresOn) return <span className={styles.muted}>No expiry</span>;

  const expiry = new Date(`${expiresOn}T00:00:00`);
  const days = Math.ceil((expiry.getTime() - Date.now()) / 86_400_000);

  if (days < 0) return <span className={styles.expired}>Expired</span>;
  if (days <= EXPIRING_SOON_DAYS) {
    return (
      <span className={styles.expiringSoon}>
        {days === 0 ? 'Expires today' : `${days} day${days === 1 ? '' : 's'} left`}
      </span>
    );
  }
  return <span className={styles.muted}>Active</span>;
}
